import {
  Box,
  Button,
  ButtonProps,
  Flex,
  IconButton,
  Image,
  Text,
} from "@chakra-ui/react";

import { useMemo } from "react";
import {
  IoArrowBack,
  IoDownloadOutline,
  IoLocationSharp,
} from "react-icons/io5";
import { NavbarProps } from "../../@types/Navbar";
import { DEFAULT_BRAND_LOGO } from "../../constant/placeholders";
import Link from "../Link";
import HamburgerMenu from "./HamburgerMenu";
import NavbarDropdown from "./NavbarDropdown";

function Navbar({
  logo,
  navLinkItems = [],
  currentPath,
  isLoggedIn,
  loggedInUser,
  displayUserDetail = true,
  routeChangeFn,
  navContainerProps,
  logoWrapperProps,
  cinemaHallWrapperProps,
  logoProps,
  onLogout,
  displayCinemaHallButton,
  activeCinemaHall,
  onCinemaHallChange,
  showRegisterButton = false,
  loginPageRoute,
  registerPageRoute,
  navLinkWrapperProps,
  search,
  navLinkButtonProps,
  navLinkButtonActiveProps,
  navDropDownProps,
  navActionButtonProps,
  navContainerWrapperProps,
  hamburgerMenuProps,
  hamburgerWrapperProps,
  navIconWrapperProps,
  iconProps,
  actionButtonWrapperProps,
  cinemaHallSelectorButtonProps,
  greetingTextProps,
  showInstallButton = false,
  onInstall,
  installButtonLabel = "Install",
  installButtonProps,
  showBackButton = false,
  onBack,
  backButtonProps,
  logoPosition = "left",
}: NavbarProps) {
  const isCenterLogo = logoPosition === "center";
  const actionButtonProps = useMemo(() => {
    return {
      className: "navbar__button",
      size: {
        base: "xs",
        md: "sm",
      },
      colorScheme: "primary",
      borderRadius: "none",
      ...navActionButtonProps,
    };
  }, [navActionButtonProps]);

  return (
    <Box {...navContainerWrapperProps}>
      <Flex
        // as="nav"
        className="navbar"
        justifyContent={{ base: "space-between", md: "center" }}
        alignItems="center"
        position={isCenterLogo ? { base: "relative", xl: "static" } : undefined}
        {...navContainerProps}
      >
        {showBackButton && (
          <IconButton
            aria-label="Go back"
            icon={<IoArrowBack size={20} />}
            variant="ghost"
            size="sm"
            onClick={() => onBack?.()}
            className="navbar__back"
            {...backButtonProps}
          />
        )}
        <Box
          as={Link}
          href="/"
          // flexBasis="15%"
          className="navbar__brand__wrapper"
          {...(isCenterLogo && {
            // Center the logo only on the mobile / PWA app bar. On desktop
            // (>= xl) the horizontal nav links occupy the center, so the logo
            // stays in its normal left position.
            position: { base: "absolute", xl: "static" },
            left: { base: "50%", xl: "auto" },
            transform: { base: "translateX(-50%)", xl: "none" },
          })}
          {...logoWrapperProps}
        >
          <Image
            maxH={{
              base: "2rem",
              md: "4rem",
            }}
            minH="2rem"
            className="navbar__brand__logo"
            id="brand_logo"
            src={logo || DEFAULT_BRAND_LOGO}
            alt="Logo"
            {...logoProps}
          />
        </Box>

        {displayCinemaHallButton && (
          <Box {...cinemaHallWrapperProps}>
            <Button
              leftIcon={<IoLocationSharp size={14} />}
              variant="ghost"
              color="gray.600"
              backgroundColor={"#dff1fcff"}
              fontWeight="normal"
              border="1px solid #ACE1FF"
              ml={"10px"}
              borderRadius={"10px"}
              size={{
                base: "xs",
                md: "sm",
                lg: "md",
              }}
              onClick={() => onCinemaHallChange()}
              {...cinemaHallSelectorButtonProps}
            >
              {activeCinemaHall}
            </Button>
          </Box>
        )}

        <Box
          background="background.nav"
          flex={1}
          className="navbar__links"
          display={{
            base: "none",
            xl: "flex",
          }}
          gap={"20px"}
          {...navLinkWrapperProps}
          overflowWrap={"normal"}
          mx={"20px"}
        >
          {search}
          {navLinkItems?.map((item, index) => {
            if (item?.authRequired && !isLoggedIn) return null;
            const isActive = item.label === currentPath;

            const navButtonActiveProps = {
              _hover: {
                background: "primary.600",
                color: "white",
              },
              colorScheme: "primary",
              color: "white",
              ...navLinkButtonActiveProps,
            };

            const navButtonProps: ButtonProps = {
              key: `nav_link_${index}`,
              _hover: {
                backgroundColor: "white",
                color: "primary.500",
              },
              size: "sm",
              fontWeight: 600,
              color: "gray.700",
              colorScheme: "transparent",
              sx: {
                textTransform: "capitalize",
              },
              px: "1.5rem",
              borderRadius: "none",
              ...navLinkButtonProps,
              onClick: () => {
                item.href && routeChangeFn(item.href, item?.target);
              },
              ...(isActive && navButtonActiveProps),
            };

            if (item.subLinks && item.subLinks.length > 0) {
              return (
                <NavbarDropdown
                  {...navDropDownProps}
                  triggerButtonProps={{
                    ...navButtonProps,
                    ...navDropDownProps?.triggerButtonProps,
                  }}
                  isLoggedIn={isLoggedIn}
                  subLinks={item.subLinks}
                  key={`nav_link_${index}`}
                  label={item.label}
                  routeChangeFn={routeChangeFn}
                />
              );
            }

            return (
              <Button
                {...navButtonProps}
                display="flex"
                flexDirection="column"
                alignItems="center"
                h="auto"
                py={2}
                gap={1}
              >
                {item.icon && (
                  <Box as="span" fontSize="xl" {...navIconWrapperProps}>
                    {typeof item.icon === "string" ? (
                      item.icon.trim().startsWith("<svg") ? (
                        <Box
                          dangerouslySetInnerHTML={{ __html: item.icon }}
                          sx={{
                            "& svg": {
                              width: "20px",
                              height: "20px",
                              stroke: "currentColor",
                              fill: "currentColor",
                            },
                          }}
                        />
                      ) : (
                        <Image
                          src={item.icon}
                          alt={item.label}
                          boxSize="20px"
                          {...iconProps}
                        />
                      )
                    ) : typeof item.icon === "function" ? (
                      <Box as={item.icon} />
                    ) : (
                      item.icon
                    )}
                  </Box>
                )}
                {item.label}
              </Button>
            );
          })}
        </Box>

        <Flex alignItems="center" gap={"2rem"}>
          {showInstallButton && (
            <Button
              className="navbar__install"
              size={{ base: "xs", md: "sm" }}
              colorScheme="primary"
              borderRadius="none"
              leftIcon={<IoDownloadOutline size={16} />}
              onClick={() => onInstall?.()}
              {...installButtonProps}
            >
              {installButtonLabel}
            </Button>
          )}
          {!isLoggedIn && (
            <Box
              display={{
                base: "none",
                xl: "flex",
              }}
              {...actionButtonWrapperProps}
            >
              <Button
                {...actionButtonProps}
                onClick={() => routeChangeFn(loginPageRoute || "/login")}
              >
                Login
              </Button>
              {showRegisterButton && (
                <Button
                  {...actionButtonProps}
                  onClick={() =>
                    routeChangeFn(registerPageRoute || "/register")
                  }
                >
                  Register
                </Button>
              )}
            </Box>
          )}
          {isLoggedIn && (
            <Box
              display={{ base: "none", md: "flex" }}
              alignItems="center"
              gap="1rem"
              {...actionButtonWrapperProps}
            >
              {loggedInUser && displayUserDetail && (
                <>
                  <Text color="primary.500" {...greetingTextProps}>
                    {(loggedInUser.name || loggedInUser.email) &&
                      "Welcome " + (loggedInUser.name || loggedInUser.email)}
                  </Text>
                </>
              )}
              <Button
                onClick={onLogout}
                display={{ base: "none", xl: "flex" }}
                {...actionButtonProps}
              >
                Logout
              </Button>
            </Box>
          )}
          <Flex
            display={{
              base: "block",
              xl: "none",
            }}
            alignItems="center"
            ml="1rem"
            {...hamburgerWrapperProps}
          >
            <HamburgerMenu
              currentPath={currentPath}
              navLinkItems={navLinkItems}
              isLoggedIn={isLoggedIn}
              routeChangeFn={routeChangeFn}
              logo={logo}
              {...hamburgerMenuProps}
              onLogout={onLogout}
              onLoginClick={() => routeChangeFn(loginPageRoute || "/login")}
              onRegisterClick={() =>
                routeChangeFn(registerPageRoute || "/register")
              }
            />
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
}

export default Navbar;
